import { IonButton, IonButtons, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLoading, IonPage, IonRow, IonTitle, IonToolbar, useIonAlert, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonList, IonLabel, IonCheckbox, IonText, IonThumbnail, IonNote } from "@ionic/react"
import { arrowBackOutline} from "ionicons/icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../../services/redux/hook";
import { isStellarAccount } from "../../../services/stellar/stellar-utils";
import { PaymentDestinationInfo, BeneficiaryRequestParams, BeneficiaryResponse, UserInfo } from "../../../types/types";
import { setPaymentDestinationInfo } from "../../../services/redux/slices/paymentDestinationInfo/paymentDestinationInfo";
import { useGetBeneficiariesQuery, useAddBeneficiaryMutation, useDeleteBeneficiaryMutation, useUpdateBeneficiaryMutation, useGetUserInfoQuery } from "../../../services/api/ebioro/ebioro";

import AmountPage from "../../amount/AmountPage";


interface ExternalRecipientPageProps {
  beneficiary?: BeneficiaryResponse;
}

const ExternalRecipientPage: React.FC<ExternalRecipientPageProps> = ({ beneficiary }) => {

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const [stellarAddress, setStellarAddress] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [nickname, setNickname] = useState<string>('');
  const [saveBeneficiary, setSaveBeneficiary] = useState<boolean>(false);
  const beneficiaryType = 'individual'; // Always individual by default

  const [showLoading, setShowLoading] = useState(false);

  const walletInfo = useAppSelector((state) => state.walletInfo);
  const userInfo: UserInfo = useAppSelector((state) => state.user);

  // API Hooks
  const { data: beneficiaries = [], isLoading: beneficiariesLoading } = useGetBeneficiariesQuery(userInfo.id, { skip: !userInfo.id });
  const [addBeneficiary, { isLoading: addingBeneficiary }] = useAddBeneficiaryMutation();
  const [updateBeneficiary, { isLoading: updatingBeneficiary }] = useUpdateBeneficiaryMutation();
  const [deleteBeneficiary] = useDeleteBeneficiaryMutation();

  // Determine if we're in edit mode
  const isEditMode = !!beneficiary;

  const nav = document.querySelector("ion-nav");

  const [present] = useIonAlert();

  const presentErrorAlert = (error: string) => {
    present({
      header: "Error",
      message: `${error}`,
      buttons: [
        {
          text: "Close",
          role: "cancel",
          handler: () => {
            return;
          },
        },
      ],
    });
  };

  const evaluateContent = async (stellarAddress: string, memo: string): Promise<React.FC<{}> | undefined> => {

    const response = await validateStellarAddress(stellarAddress);

    if (response) {

      // Save or update beneficiary if requested
      if (saveBeneficiary && nickname.trim()) {
        try {
          const beneficiaryParams: BeneficiaryRequestParams = {
            type: beneficiaryType,
            account_type: 'stellar_wallet',
            user_id: userInfo.id,
            stellar_account: stellarAddress,
            payment_id_name: nickname.trim(),
            memo: memo.trim() || undefined,
            memo_type: memo.trim() ? 'text' : undefined,
          };

          if (isEditMode && beneficiary?.id) {
            // Update existing beneficiary
            await updateBeneficiary({
              id: beneficiary.id,
              ...beneficiaryParams
            }).unwrap();
          } else {
            // Add new beneficiary
            await addBeneficiary(beneficiaryParams).unwrap();
          }
        } catch (error) {
          presentErrorAlert(`Failed to ${isEditMode ? 'update' : 'save'} beneficiary`);
        }
      }

      const updatedPaymentInfo: PaymentDestinationInfo = {
        destination_address: stellarAddress,
        destination_username: username ? { name: username } : undefined,
        destination_memo: memo ? memo : undefined,
        destination_memo_type: 'text',
        destination_amount: undefined,
        destination_asset: undefined
      }

      dispatch(setPaymentDestinationInfo(updatedPaymentInfo));
      goToAmountPage();
      return;

    }

  }

  const validateStellarAddress = async (value: string): Promise<boolean> => {

    if (!value) {
      return false;
    }

    const isAccount = isStellarAccount(value);

    if (!isAccount) {
      const error = t("recipient.page.invalid.STELLAR-ACCOUNT-ERROR");
      presentErrorAlert(error);
      return false;
    }

    if (isAccount && walletInfo.account_key === value) {

      const error = t("barcodereader.page.invalid.SAME-RECIPIENT-ERROR");
      presentErrorAlert(error);
      return false;
    }

    else {

      return true;

    }

  };

  const goToAmountPage = async () => {

    nav?.push(() => <AmountPage />);

  };

  // Populate fields when editing existing beneficiary
  useEffect(() => {
    if (isEditMode && beneficiary) {
      setStellarAddress(beneficiary.stellar_account || '');
      setMemo(beneficiary.memo || '');
      setNickname(beneficiary.payment_id_name || '');
      // Note: beneficiaryType is always 'individual' now
      setSaveBeneficiary(true); // Always true when editing
    }

    return () => {
      setStellarAddress('')
    };

  }, [isEditMode, beneficiary])

  return (
    <IonPage>
      <IonHeader className="ion-no-border">

        <IonToolbar style={{ marginTop: "0%" }}>
          <IonButtons slot='start'>
            <IonButton color='primary' onClick={() => { (nav?.pop()) }}>
              <IonIcon slot="icon-only" icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>{isEditMode ? 'Edit External Wallet' : t('addRecipientExternal.HEADER') as string}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>



        {/* New External Wallet Form */}
        <IonCard>
          <IonCardHeader>
            <IonCardSubtitle>{isEditMode ? 'Edit External Wallet' : t("addRecipientExternal.ADD_NEW_WALLET_LABEL") as string}</IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>

            <IonGrid className='recipientStellarAccount'>

              {/* Nickname field */}
              <IonRow>
                <IonCol size="12">
                  <IonItem lines="none" style={{ marginTop: '5%' }}
                    color={'var(--ion-background-color)'}>
                    <IonInput
                      fill="outline"
                      mode="md"
                      style={{ textAlign: 'center' }}
                      value={nickname}
                      clearOnEdit={true}
                      onIonInput={(e: any) => setNickname(e.detail.value!)}
                      placeholder={t("addRecipientExternal.NICKNAME_PLACEHOLDER") as string}
                      debounce={300}
                    />
                  </IonItem>
                </IonCol>
              </IonRow>

              <IonRow>
                <IonCol size="12">

                  <IonItem lines="none"
                    color={'var(--ion-background-color)'}
                  >
                    <IonInput fill="outline" mode="md" style={{ textAlign: 'center', marginTop: '5%' }}
                      value={stellarAddress}
                      clearOnEdit={true}
                      onIonInput={(e: any) =>
                        setStellarAddress(e.detail.value!)
                      }
                      placeholder={t("recipient.page.ENTER-ADDRESS-PLACEHOLDER") as string}
                      debounce={1000}
                    >

                    </IonInput>
                  </IonItem>

                </IonCol>

              </IonRow>
              <IonRow>
                <IonCol size="12">

                  <IonItem lines="none" style={{ marginTop: '5%' }}
                    color={'var(--ion-background-color)'}
                  >
                    <IonInput fill="outline" mode="md" style={{ textAlign: 'center' }}
                      value={memo}
                      clearOnEdit={true}
                      onIonInput={(e: any) =>
                        setMemo(e.detail.value!)
                      }
                      placeholder={t("recipient.page.MEMO_TAG-PLACEHOLDER") as string}
                      debounce={1000}
                    >

                    </IonInput>
                  </IonItem>

                </IonCol>

              </IonRow>

              {/* Save as beneficiary checkbox */}
              <IonRow>
                <IonCol size="12">
                  <IonItem lines="none" style={{ marginTop: '3%' }}>
                    <IonCheckbox
                      checked={saveBeneficiary}
                      onIonChange={(e) => setSaveBeneficiary(e.detail.checked)}
                    />
                    <IonLabel style={{ marginLeft: '10px' }}>
                      <IonText>{t("addRecipientExternal.SAVE_WALLET_LABEL") as string}</IonText>
                      {saveBeneficiary && !nickname.trim() && (
                        <IonNote color="warning" style={{ display: 'block', fontSize: '0.8em' }}>
                          {t("addRecipientExternal.NICKNAME_MISSING_WARNING") as string}
                        </IonNote>
                      )}
                    </IonLabel>
                  </IonItem>
                </IonCol>
              </IonRow>

            </IonGrid>

            <IonLoading isOpen={showLoading || addingBeneficiary || updatingBeneficiary} />
            <IonButton
              onClick={() => evaluateContent(stellarAddress, memo)}
              disabled={!stellarAddress || stellarAddress.length < 8 || (saveBeneficiary && !nickname.trim())}
              style={{ marginTop: '5%' }}
              color={'primary'}
              className="platformRecipientButtonAdd"
              type="submit"
              expand="block"
            >
              {isEditMode ? 'Update & Continue' : 'Continue'}
            </IonButton>

          </IonCardContent>
        </IonCard>

        {/* <IonItem lines="none" hidden={!invalidStellarAccount}>
          <IonLabel color="danger">
            {" "}
            {t("recipient.page.invalid.STELLAR-ACCOUNT-ERROR")}
          </IonLabel>
        </IonItem>

        <IonItem lines="none" hidden={!isSameAddress}>
          <IonLabel color="danger">
            {" "}
            {t("recipient.page.invalid.SAME-RECIPIENT-ERROR")}
          </IonLabel>
        </IonItem>

        <IonItem lines="none" hidden={isFunded}>
          <IonLabel color="danger">
            {" "}
            {t("recipient.page.invalid.DESTINATION-NOT-FUNDED-ERROR")}
          </IonLabel>
        </IonItem> */}
      </IonContent>

    </IonPage>
  )

}

export default ExternalRecipientPage